import React, { useState, useEffect } from "react";
import { KpiModal, ModalHeader } from "../../Quantative/component/KpiIcons";
import {
    uploadFileMetadata,
    createDocument,
    deleteDocument,
} from "../../../../_services/documentsService";
import QualDocUploadZone from "./QualDocUploadZone";
import DocumentPreviewModal from "../../components/DocumentPreviewModal";

const injectModalStyles = () => {
    if (document.getElementById("qual-doc-modal-styles")) return;
    const s = document.createElement("style");
    s.id = "qual-doc-modal-styles";
    s.textContent = `
        :root {
            --ff:'DM Sans','Segoe UI',sans-serif;
            --fm:'JetBrains Mono','Fira Mono',monospace;
            --bdr:#e4e8f0; --bdr2:#f0f2f7;
            --ink:#0f172a; --ink2:#334155; --ink3:#64748b; --ink4:#94a3b8;
            --blue:#3b82f6; --blue2:#bfdbfe; --blue3:#eff6ff;
            --red:#dc2626; --red2:#fee2e2;
            --green:#16a34a;
        }
        @keyframes qdmSpin { to { transform:rotate(360deg); } }
        .qdm-spin {
            animation:qdmSpin .7s linear infinite;
            display:inline-block;border-radius:50%;flex-shrink:0;
            width:13px;height:13px;
            border:2px solid rgba(255,255,255,0.35);border-top-color:#fff;
        }
        .qdm-btn {
            display:inline-flex;align-items:center;gap:6px;padding:0 16px;height:36px;
            border-radius:8px;font-size:12.5px;font-weight:600;cursor:pointer;border:none;
            font-family:var(--ff);transition:all .15s;white-space:nowrap;
        }
        .qdm-btn-primary { background:var(--blue);color:#fff; }
        .qdm-btn-primary:hover:not(:disabled) { background:#2563eb; }
        .qdm-btn-primary:disabled { opacity:0.5;cursor:not-allowed; }
        .qdm-btn-ghost { background:transparent;color:var(--ink3);border:1.5px solid var(--bdr); }
        .qdm-btn-ghost:hover:not(:disabled) { background:var(--bdr2);color:var(--ink2); }
        .qdm-btn-ghost:disabled { opacity:0.5;cursor:not-allowed; }
        .qdm-btn-danger { background:var(--red);color:#fff; }
        .qdm-btn-danger:hover:not(:disabled) { background:#b91c1c; }
        .qdm-btn-danger:disabled { opacity:0.5;cursor:not-allowed; }
        .qdm-btn-danger-ghost {
            background:transparent;color:var(--red);
            border:1.5px solid #fca5a5;font-size:11px;
            padding:0 10px;height:28px;
        }
        .qdm-btn-danger-ghost:hover:not(:disabled) { background:var(--red2); }
        .qdm-btn-sm-ghost {
            background:transparent;color:var(--ink3);
            border:1.5px solid var(--bdr);font-size:11px;
            padding:0 10px;height:28px;
        }
        .qdm-btn-sm-ghost:hover:not(:disabled) { background:var(--bdr2);color:var(--ink2); }
        .qdm-btn-sm-ghost:disabled { opacity:0.5;cursor:not-allowed; }
        /* doc list item */
        .qdm-doc-item {
            border:1px solid var(--bdr);border-radius:9px;
            margin-bottom:8px;overflow:hidden;
            transition:border-color .15s;
        }
        .qdm-doc-item-row {
            display:flex;align-items:center;gap:10px;
            padding:10px 14px;min-width:0;
        }
        .qdm-doc-item-actions {
            display:flex;gap:6px;flex-shrink:0;
        }
        .qdm-confirm-bar {
            display:flex;align-items:center;justify-content:space-between;gap:10px;
            padding:8px 14px;
            background:var(--red2);border-top:1px solid #fca5a5;
        }
        .qdm-replace-zone {
            padding:8px 14px 12px;
            border-top:1px solid var(--bdr2);
            background:var(--bdr2);
        }
        .qdm-err {
            display:flex;align-items:center;gap:6px;margin-top:8px;
            padding:8px 12px;border-radius:7px;
            background:var(--red2);border:1px solid #fca5a5;
            font-size:11.5px;color:var(--red);font-weight:600;
        }
        .qdm-section-label {
            font-size:10.5px;font-weight:700;color:var(--ink3);
            text-transform:uppercase;letter-spacing:.06em;font-family:var(--fm);
            margin-bottom:8px;user-select:none;
        }
        .qdm-empty {
            text-align:center;padding:20px 16px;
            font-size:12px;color:var(--ink4);font-style:italic;
            border:1.5px dashed var(--bdr);border-radius:9px;margin-bottom:12px;
        }
        @keyframes qdmFadeIn { from { opacity:0;transform:translateY(-4px); } to { opacity:1;transform:translateY(0); } }
        .qdm-doc-item { animation:qdmFadeIn .2s ease; }
    `;
    document.head.appendChild(s);
};

/**
 * Builds the SaveDocumentDto payload for a qualitative evidence document —
 * mirrors the values the qualitative answer-save flow sets server-side
 * (data_management.service.ts's persistQualitativeAnswer: documentType
 * EVIDENCE, questionType QUALITATIVE, shouldParse false since only
 * quantitative evidence is analyzed by DocSense) so uploads made from this
 * modal land in the same bucket as documents attached while answering.
 */
const buildEvidencePayload = (questionId, financialYearId, fileMetadataId, fileName) => ({
    documentType: "EVIDENCE",
    financialYearId: Number(financialYearId),
    questionType: "qualitative",
    questionId: Number(questionId),
    fileName,
    fileMetadataId,
    shouldParse: false,
});

const UploadIcon = ({ color = "var(--blue)", size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ flexShrink: 0, width: size, height: size }}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
);

const SpinnerInline = () => (
    <span style={{
        display: "inline-block", width: 10, height: 10, borderRadius: "50%",
        border: "2px solid var(--blue2)", borderTopColor: "var(--blue)",
        animation: "qdmSpin .7s linear infinite", flexShrink: 0,
    }} />
);

/**
 * Props:
 *   show            {boolean}
 *   onHide          {function}
 *   onSuccess       {function(docs: object[])}  — called with updated docs array after any mutation
 *   questionId      {number|string}
 *   questionTitle   {string}
 *   financialYearId {number|string}  — required to build a valid evidence document payload
 *   documents       {object[]}    — array of DocumentDto objects fetched by QualitativeRow
 *   canUpload       {boolean}     — false renders a view-only list (no add/replace/delete)
 */
const QualitativeDocumentUploadModal = ({
    show, onHide, onSuccess,
    questionId, questionTitle, financialYearId,
    documents = [],
    canUpload = true,
}) => {
    useEffect(() => { injectModalStyles(); }, []);

    // ── Document list state ──
    const [fetchedDocs, setFetchedDocs] = useState([]);

    // ── Bulk-add (POST) state ──
    const [pendingFiles, setPendingFiles]   = useState([]);
    const [uploading, setUploading]         = useState(false);
    const [uploadErr, setUploadErr]         = useState("");

    // ── Per-row replace (PUT) state ──
    const [replacingDocId, setReplacingDocId]       = useState(null);
    const [replacePendingFile, setReplacePendingFile] = useState(null);
    const [replaceUploading, setReplaceUploading]   = useState(false);
    const [replaceErr, setReplaceErr]               = useState("");

    // ── Per-row delete state ──
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [deletingDocId, setDeletingDocId]     = useState(null);

    // ── Preview (shared modal, same as everywhere else documents are shown) ──
    const [previewDoc, setPreviewDoc] = useState(null);

    // Sync from parent each time modal opens
    useEffect(() => {
        if (show) {
            setFetchedDocs(Array.isArray(documents) ? documents : []);
            setPendingFiles([]);
            setUploadErr("");
            setReplacingDocId(null);
            setReplacePendingFile(null);
            setReplaceErr("");
            setConfirmDeleteId(null);
        }
    }, [show, documents]);

    // ── Helpers ──
    const docFileName = (doc) =>
        doc?.fileMetadata?.fileName ?? doc?.fileName ?? "Document";
    const docUrl = (doc) =>
        doc?.fileMetadata?.url ?? doc?.url ?? null;
    const docId = (doc) => doc?.id ?? doc?.documentId;

    const anyBusy = uploading || replaceUploading || !!deletingDocId;

    // ── Bulk add (uploadFileMetadata + createDocument per file) ──
    const handleBulkUpload = async () => {
        if (!pendingFiles.length) return;
        setUploading(true);
        setUploadErr("");

        const outcomes = await Promise.all(
            pendingFiles.map(async (file) => {
                const { fileMetadataId, fileMetadata } = await uploadFileMetadata(file);
                if (!fileMetadataId) return { file, document: null };
                const created = await createDocument(
                    buildEvidencePayload(questionId, financialYearId, fileMetadataId, fileMetadata?.fileName ?? file.name)
                );
                return { file, document: created?.id ? created : null };
            })
        );

        setUploading(false);

        const newDocs = outcomes.filter((o) => o.document).map((o) => o.document);
        const failedFiles = outcomes.filter((o) => !o.document).map((o) => o.file);

        if (newDocs.length) {
            const merged = [...fetchedDocs, ...newDocs];
            setFetchedDocs(merged);
            onSuccess?.(merged);
        }

        // Don't hard-block on partial failures — keep the ones that succeeded,
        // leave the failed subset pending so the user can retry just those.
        setPendingFiles(failedFiles);
        setUploadErr(
            failedFiles.length
                ? newDocs.length
                    ? `${failedFiles.length} of ${pendingFiles.length} file(s) failed to upload. The rest were attached.`
                    : "Upload failed. Please try again."
                : ""
        );
    };

    // ── Replace (single) — the new Documents API has no in-place file swap,
    // so this creates the new evidence document first and only deletes the
    // old one once the new one is confirmed saved. ──
    const handleReplace = async () => {
        if (!replacePendingFile || !replacingDocId) return;
        setReplaceUploading(true);
        setReplaceErr("");

        const { fileMetadataId, fileMetadata } = await uploadFileMetadata(replacePendingFile);
        if (!fileMetadataId) {
            setReplaceUploading(false);
            setReplaceErr("Replace failed. Please try again.");
            return;
        }

        const created = await createDocument(
            buildEvidencePayload(questionId, financialYearId, fileMetadataId, fileMetadata?.fileName ?? replacePendingFile.name)
        );
        if (!created?.id) {
            setReplaceUploading(false);
            setReplaceErr("Replace failed. Please try again.");
            return;
        }

        await deleteDocument(replacingDocId);
        setReplaceUploading(false);

        const updated = fetchedDocs.map((d) =>
            docId(d) === replacingDocId ? created : d
        );
        setFetchedDocs(updated);
        setReplacingDocId(null);
        setReplacePendingFile(null);
        onSuccess?.(updated);
    };

    // ── Delete (single) ──
    const handleDelete = async (id) => {
        setDeletingDocId(id);
        const result = await deleteDocument(id);
        setDeletingDocId(null);
        setConfirmDeleteId(null);

        if (result && !result.error) {
            const updated = fetchedDocs.filter((d) => docId(d) !== id);
            setFetchedDocs(updated);
            onSuccess?.(updated);
        }
    };

    if (!show) return null;

    return (
        <KpiModal show={show} onClose={onHide} maxWidth={540}>
            <ModalHeader
                icon="upload"
                title="Documents"
                titleStyle={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: "#3346c2",
                    fontFamily: "var(--font-mono)",
                    textTransform: "uppercase",
                    letterSpacing: "0.09em",
                }}
                subtitle={questionTitle}
                subtitleStyle={{
                    fontSize: 15.5,
                    fontWeight: 700,
                    color: "var(--ink)",
                    fontFamily: "var(--font-sans)",
                    maxWidth: "none",
                    lineHeight: 1.45,
                    letterSpacing: "-0.01em",
                }}
                bg="linear-gradient(135deg,#f8faff 0%,#f1f5fd 100%)"
                borderColor="#dbe4fd"
                onClose={onHide}
            />

            <div style={{ padding: "16px 24px 24px" }}>

                {/* ══ DOCUMENT LIST ══ */}
                <div className="qdm-section-label">
                    Attached Documents ({fetchedDocs.length})
                </div>

                {fetchedDocs.length === 0 ? (
                    <div className="qdm-empty">No documents attached yet.</div>
                ) : (
                    fetchedDocs.map((doc) => {
                        const id   = docId(doc);
                        const name = docFileName(doc);
                        const url  = docUrl(doc);
                        const isReplacing  = replacingDocId === id;
                        const isConfirming = confirmDeleteId === id;
                        const isDeleting   = deletingDocId === id;

                        return (
                            <div key={id} className="qdm-doc-item">
                                {/* Main row */}
                                <div className="qdm-doc-item-row">
                                    <UploadIcon />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink2)", wordBreak: "break-all" }}>
                                            {name}
                                        </span>
                                    </div>
                                    {/* Open/Preview — same pair used everywhere else a document is shown
                                        (DocumentViewerPanel, MultiDocumentUploadField) — always visible,
                                        even in the read-only View Documents state. */}
                                    <div className="qdm-doc-item-actions">
                                        {url && (
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="qdm-btn qdm-btn-sm-ghost"
                                                style={{ textDecoration: "none" }}
                                            >
                                                ↗ Open
                                            </a>
                                        )}
                                        {url && (
                                            <button
                                                className="qdm-btn qdm-btn-sm-ghost"
                                                onClick={() => setPreviewDoc(doc)}
                                            >
                                                👁 Preview
                                            </button>
                                        )}
                                    </div>
                                    {canUpload && (
                                    <div className="qdm-doc-item-actions">
                                        {/* Replace toggle */}
                                        {!isConfirming && (
                                            <button
                                                className="qdm-btn qdm-btn-sm-ghost"
                                                disabled={anyBusy}
                                                onClick={() => {
                                                    if (isReplacing) {
                                                        setReplacingDocId(null);
                                                        setReplacePendingFile(null);
                                                        setReplaceErr("");
                                                    } else {
                                                        setReplacingDocId(id);
                                                        setReplacePendingFile(null);
                                                        setReplaceErr("");
                                                    }
                                                }}
                                            >
                                                {isReplacing ? "Cancel" : "Replace"}
                                            </button>
                                        )}
                                        {/* Delete trigger */}
                                        {!isReplacing && (
                                            <button
                                                className="qdm-btn qdm-btn-danger-ghost"
                                                disabled={anyBusy}
                                                onClick={() => setConfirmDeleteId(isConfirming ? null : id)}
                                            >
                                                {isDeleting
                                                    ? <><SpinnerInline /> Deleting…</>
                                                    : isConfirming ? "Cancel" : "Delete"
                                                }
                                            </button>
                                        )}
                                    </div>
                                    )}
                                </div>

                                {/* Inline delete confirm */}
                                {isConfirming && !isReplacing && (
                                    <div className="qdm-confirm-bar">
                                        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--red)" }}>
                                            Remove this document?
                                        </span>
                                        <button
                                            className="qdm-btn qdm-btn-danger"
                                            style={{ height: 28, fontSize: 11, padding: "0 14px" }}
                                            disabled={isDeleting}
                                            onClick={() => handleDelete(id)}
                                        >
                                            {isDeleting
                                                ? <><span className="qdm-spin" /> Deleting…</>
                                                : "Yes, Delete"
                                            }
                                        </button>
                                    </div>
                                )}

                                {/* Inline replace zone */}
                                {isReplacing && (
                                    <div className="qdm-replace-zone">
                                        <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--ink3)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>
                                            Replace with new file
                                        </div>
                                        <QualDocUploadZone
                                            multi={false}
                                            pendingFile={replacePendingFile}
                                            onFile={(f) => { setReplacePendingFile(f); setReplaceErr(""); }}
                                            onClear={() => setReplacePendingFile(null)}
                                            disabled={replaceUploading}
                                            uploading={replaceUploading}
                                            uploadErr={replaceErr}
                                            onAttach={handleReplace}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}

                {/* ══ ADD DOCUMENTS (bulk POST) ══ */}
                {canUpload && (
                    <>
                        <div className="qdm-section-label" style={{ marginTop: 18 }}>
                            Add Documents
                        </div>
                        <QualDocUploadZone
                            multi={true}
                            pendingFiles={pendingFiles}
                            onFile={(files) => {
                                setPendingFiles((prev) => [...prev, ...files]);
                                setUploadErr("");
                            }}
                            onRemoveFile={(idx) =>
                                setPendingFiles((prev) => prev.filter((_, i) => i !== idx))
                            }
                            onClear={() => setPendingFiles([])}
                            disabled={anyBusy}
                            uploading={uploading}
                            uploadErr={uploadErr}
                            onAttach={pendingFiles.length ? handleBulkUpload : undefined}
                        />
                        {uploadErr && pendingFiles.length === 0 && (
                            <div className="qdm-err">⚠ {uploadErr}</div>
                        )}
                    </>
                )}

                {/* ── Footer ── */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                    <button className="qdm-btn qdm-btn-ghost" disabled={anyBusy} onClick={onHide}>
                        Close
                    </button>
                </div>
            </div>

            <DocumentPreviewModal document={previewDoc} onClose={() => setPreviewDoc(null)} variant="kpi" />
        </KpiModal>
    );
};

export default QualitativeDocumentUploadModal;
